import React, { useState, useEffect } from 'react';
import { usePWA } from '../../contexts/PWAContext';
import { toast } from 'react-hot-toast';
import { useBrand } from '../../contexts/BrandContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Download, X } from 'lucide-react';
import { Z_INDEX } from '../../utils/zIndex';

const PWAInstallPrompt: React.FC = () => {
  const { isPWASupported, isPWAInstalled, isInstallPromptAvailable, showInstallPrompt } = usePWA();
  const [showPrompt] = useLocalStorage<boolean>('pwa.installPrompt.enabled', true);
  const { brandColors } = useBrand();
  const [isVisible, setIsVisible] = useState(false);
  const [hideTimer, setHideTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!showPrompt || !isPWASupported || isPWAInstalled || !isInstallPromptAvailable) return;

    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) return;

    const later = localStorage.getItem('pwa-install-later');
    if (later && Date.now() - parseInt(later || '0') < 24 * 60 * 60 * 1000) return;

    setIsVisible(true);

    // Auto-hide after 10 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 10000);

    setHideTimer(timer);

    return () => {
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [isPWASupported, isPWAInstalled, isInstallPromptAvailable, showPrompt]);

  const handleInstall = async () => {
    try {
      await showInstallPrompt();
      toast.success('App installed successfully!');
      setIsVisible(false);
      localStorage.setItem('pwa-install-dismissed', 'true');
    } catch (error) {
      toast.error('Installation failed. Try again.');
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!isVisible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-12 left-1/2 -translate-x-1/2 w-11/12 max-w-sm z-[120] pointer-events-auto transition-all duration-300 ease-in-out opacity-100 scale-100"
      style={{ zIndex: Z_INDEX.TOAST }}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <Card className="border-0 shadow-2xl backdrop-blur-md overflow-hidden animate-in slide-in-from-bottom-2 duration-300 fade-in-0"
        style={{
          background: `linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 50%, var(--brand-accent) 100%)`,
        }}
      >
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
              <Download className="w-5 h-5 text-white drop-shadow-sm" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-white text-sm leading-tight mb-0.5 truncate">Install Ghana Town App</h3>
              <p className="text-white/90 text-xs leading-tight">Offline access, faster loading, notifications</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              size="sm"
              className="h-9 px-4 bg-white/20 backdrop-blur-sm hover:bg-white/30 border-white/30 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              onClick={handleInstall}
            >
              Install
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-9 w-9 p-0 hover:bg-white/20 text-white/80 hover:text-white"
              onClick={handleDismiss}
              data-testid="dismiss-prompt"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
